import './config-tab.css';

import {
  BUNDLE_INDEX_COLUMNS,
  type BundleIndexColumnId,
} from '../../../../../../backend/src/matter/subset/bundle/bundle-index-layout';
import type {
  BundleFontFamily,
  BundleTextColor,
} from '../../../../../../backend/src/matter/subset/bundle/dto/export-bundle.dto';
import { ColumnsField } from '@/new/components/download/fields/columns-field';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Switch } from '@/new/components/shadcn/switch';
import { TooltipIcon } from '@/new/components/tooltip-icon';

export type { BundleFontFamily, BundleTextColor };

const COLUMN_LABEL_BY_ID: Record<BundleIndexColumnId, string> = Object.fromEntries(
  BUNDLE_INDEX_COLUMNS.map((c) => [c.id, c.label]),
) as Record<BundleIndexColumnId, string>;
const COLUMN_ID_BY_LABEL: Record<string, BundleIndexColumnId> = Object.fromEntries(
  BUNDLE_INDEX_COLUMNS.map((c) => [c.label, c.id]),
);
const ALL_COLUMN_LABELS = BUNDLE_INDEX_COLUMNS.map((c) => c.label);

export interface PageNumberPos {
  x: number;
  y: number;
}

export interface BundleConfig {
  name: string;
  subheading: string;
  pageNumberPosition: PageNumberPos;
  fontFamily: BundleFontFamily;
  fontSize: number;
  pageNumberSize: number;
  textColor: BundleTextColor;
  pageNumberBold: boolean;
  pageNumberItalic: boolean;
  pageNumberUnderline: boolean;
  numberIndexPages: boolean;
  useSmallRomanIndexNumbering: boolean;
  continuePageNumberingAfterIndex: boolean;
  repeatIndexPageTitle: boolean;
  columns: BundleIndexColumnId[];
}

interface ConfigTabProps {
  config: BundleConfig;
  onConfigChange: (config: BundleConfig) => void;
}

const pageNumberPositionOptions: Array<{
  id: string;
  label: string;
  position: PageNumberPos;
}> = [
  { id: 'top-left', label: 'Top left', position: { x: 0, y: 0 } },
  { id: 'top-middle', label: 'Top middle', position: { x: 50, y: 0 } },
  { id: 'top-right', label: 'Top right', position: { x: 100, y: 0 } },
  { id: 'center', label: 'Center', position: { x: 50, y: 50 } },
  { id: 'bottom-left', label: 'Bottom left', position: { x: 0, y: 100 } },
  { id: 'bottom-middle', label: 'Bottom middle', position: { x: 50, y: 100 } },
  { id: 'bottom-right', label: 'Bottom right', position: { x: 100, y: 100 } },
];

const FONT_FAMILY_STACKS: Record<BundleFontFamily, string> = {
  times: "'Times New Roman', Times, serif",
  helvetica: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  courier: "'Courier New', Courier, monospace",
};

const TEXT_COLOR_HEX: Record<BundleTextColor, string> = {
  black: '#000000',
  gray: '#6b7280',
  blue: '#2563eb',
  red: '#dc2626',
};

function ClampedNumberField({
  id,
  label,
  value,
  min,
  max,
  fallback,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  fallback: number;
  onChange: (next: number) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-xs text-stone-500">
        {label}
      </Label>
      <Input
        key={value}
        id={id}
        type="number"
        min={min}
        max={max}
        defaultValue={value}
        onBlur={(event) => {
          const parsed = Number(event.target.value);
          const next =
            Number.isFinite(parsed) && event.target.value.trim() !== ''
              ? Math.max(min, Math.min(max, parsed))
              : fallback;
          event.currentTarget.value = String(next);
          if (next !== value) onChange(next);
        }}
        className="border-stone-300 bg-white"
      />
    </div>
  );
}

function PageNumberPreview({ config }: { config: BundleConfig }) {
  const { x, y } = config.pageNumberPosition;

  return (
    <div className="space-y-2">
      <Label className="text-xs text-stone-500">Preview</Label>
      <div className="relative mx-auto aspect-[210/297] w-full max-w-[140px] overflow-hidden rounded-sm border border-stone-300 bg-white shadow-sm">
        <div aria-hidden className="pointer-events-none absolute inset-3 border border-dashed border-stone-200" />
        <span
          style={{
            position: 'absolute',
            left: `${x}%`,
            top: `${y}%`,
            transform: `translate(-${x}%, -${y}%)`,
            color: TEXT_COLOR_HEX[config.textColor],
            fontFamily: FONT_FAMILY_STACKS[config.fontFamily],
            fontSize: '10px',
            fontWeight: config.pageNumberBold ? 700 : 400,
            fontStyle: config.pageNumberItalic ? 'italic' : 'normal',
            textDecoration: config.pageNumberUnderline ? 'underline' : 'none',
            padding: '6px',
            whiteSpace: 'nowrap',
            lineHeight: 1,
          }}
        >
          26
        </span>
      </div>
    </div>
  );
}

export const ConfigTab = ({ config, onConfigChange }: ConfigTabProps) => {
  const activePageNumberPositionId =
    pageNumberPositionOptions.find(
      ({ position }) => position.x === config.pageNumberPosition.x && position.y === config.pageNumberPosition.y,
    )?.id ?? 'bottom-middle';

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="bundle-name">Filename</Label>
        <Input
          id="bundle-name"
          value={config.name}
          onChange={(event) => onConfigChange({ ...config, name: event.target.value })}
          maxLength={200}
          placeholder="Enter filename"
          className="border-stone-300 bg-white"
        />
      </div>

      <div className="space-y-2">
        <ColumnsField
          spec={{ available: ALL_COLUMN_LABELS, default: ALL_COLUMN_LABELS }}
          columns={config.columns.map((id) => COLUMN_LABEL_BY_ID[id])}
          onColumnsChange={(labels) =>
            onConfigChange({
              ...config,
              columns: labels
                .map((label) => COLUMN_ID_BY_LABEL[label])
                .filter((id): id is BundleIndexColumnId => id !== undefined),
            })
          }
          relativityColumns={[]}
          onRelativityColumnsChange={() => {}}
        />
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <Label htmlFor="repeat-index-page-title">Repeat title on each index page</Label>
          <p className="text-xs text-stone-500">
            Show the title template above the column headers on continuation pages.
          </p>
        </div>
        <Switch
          id="repeat-index-page-title"
          checked={config.repeatIndexPageTitle}
          onCheckedChange={(repeatIndexPageTitle) => onConfigChange({ ...config, repeatIndexPageTitle })}
        />
      </div>

      <div className="space-y-3">
        <Label>Typography</Label>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs text-stone-500">Font</Label>
            <Select
              value={config.fontFamily}
              onValueChange={(value: BundleFontFamily) => onConfigChange({ ...config, fontFamily: value })}
            >
              <SelectTrigger className="border-stone-300 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="times">Times</SelectItem>
                <SelectItem value="helvetica">Helvetica</SelectItem>
                <SelectItem value="courier">Courier</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <ClampedNumberField
            id="bundle-font-size"
            label="Index size"
            value={config.fontSize}
            min={8}
            max={16}
            fallback={12}
            onChange={(fontSize) => onConfigChange({ ...config, fontSize })}
          />
        </div>
      </div>

      <div className="space-y-3">
        <Label>Page Number</Label>

        <div className="mt-1.5 flex w-full items-center gap-2">
          <Label htmlFor="number-index-pages" className="flex gap-2 space-y-2 text-xs text-stone-500">
            Number index pages
            <TooltipIcon tooltipContent="Add page numbers to the index." triggerClassName="h-3.5 w-3.5" />
          </Label>
          <Switch
            id="number-index-pages"
            className="ml-auto shrink-0"
            checked={config.numberIndexPages}
            onCheckedChange={(enabled) =>
              onConfigChange({
                ...config,
                numberIndexPages: enabled,
                useSmallRomanIndexNumbering: false,
                continuePageNumberingAfterIndex: enabled,
              })
            }
          />
        </div>

        {config.numberIndexPages && (
          <div className="space-y-2">
            <Label className="text-xs text-stone-500">Index numbering</Label>
            <Select
              value={config.useSmallRomanIndexNumbering ? 'roman' : 'arabic'}
              onValueChange={(value) =>
                onConfigChange({
                  ...config,
                  useSmallRomanIndexNumbering: value === 'roman',
                  continuePageNumberingAfterIndex: value === 'roman' ? false : config.continuePageNumberingAfterIndex,
                })
              }
            >
              <SelectTrigger className="border-stone-300 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="arabic">Arabic</SelectItem>
                <SelectItem value="roman">Roman</SelectItem>
              </SelectContent>
            </Select>

            {!config.useSmallRomanIndexNumbering && (
              <div className="flex w-full items-center gap-2 pt-1">
                <Label htmlFor="continue-page-numbering-after-index" className="flex gap-2 text-xs text-stone-500">
                  Continue numbering into bundle
                  <TooltipIcon
                    tooltipContent="Continue document page numbers after the index instead of restarting at 1."
                    triggerClassName="h-3.5 w-3.5"
                  />
                </Label>
                <Switch
                  id="continue-page-numbering-after-index"
                  className="ml-auto shrink-0"
                  checked={config.continuePageNumberingAfterIndex}
                  onCheckedChange={(continuePageNumberingAfterIndex) =>
                    onConfigChange({ ...config, continuePageNumberingAfterIndex })
                  }
                />
              </div>
            )}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-xs text-stone-500">Color</Label>
                <Select
                  value={config.textColor}
                  onValueChange={(value: BundleTextColor) => onConfigChange({ ...config, textColor: value })}
                >
                  <SelectTrigger className="border-stone-300 bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="black">Black</SelectItem>
                    <SelectItem value="gray">Gray</SelectItem>
                    <SelectItem value="blue">Blue</SelectItem>
                    <SelectItem value="red">Red</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <ClampedNumberField
                id="bundle-page-number-size"
                label="Size"
                value={config.pageNumberSize}
                min={8}
                max={24}
                fallback={12}
                onChange={(pageNumberSize) => onConfigChange({ ...config, pageNumberSize })}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-stone-500">Position</Label>
              <Select
                value={activePageNumberPositionId}
                onValueChange={(value) => {
                  const nextPosition = pageNumberPositionOptions.find((option) => option.id === value)?.position;
                  if (!nextPosition) return;
                  onConfigChange({ ...config, pageNumberPosition: nextPosition });
                }}
              >
                <SelectTrigger className="border-stone-300 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageNumberPositionOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-stone-500">Style</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onConfigChange({ ...config, pageNumberBold: !config.pageNumberBold })}
                  className={config.pageNumberBold ? 'border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100' : ''}
                >
                  Bold
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onConfigChange({ ...config, pageNumberItalic: !config.pageNumberItalic })}
                  className={
                    config.pageNumberItalic ? 'border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100' : ''
                  }
                >
                  Italic
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onConfigChange({ ...config, pageNumberUnderline: !config.pageNumberUnderline })}
                  className={
                    config.pageNumberUnderline ? 'border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100' : ''
                  }
                >
                  Underline
                </Button>
              </div>
            </div>
          </div>

          <PageNumberPreview config={config} />
        </div>
      </div>
    </div>
  );
};
